import React, { useState, useEffect } from 'react';
import AuthorFields from '../components/AuthorFields';
import CitationResult from '../components/CitationResult';
import { formatAuthorsForCitation, formatAuthorsForParenthetical, formatAuthorsForReference, formatURL } from '../utils/citationHelpers';

function EventForm() {
  const [authors, setAuthors] = useState([]);
  const [authorType, setAuthorType] = useState('personal');
  const [formData, setFormData] = useState({
    title: '',
    presentationType: 'Ponencia',
    eventName: '',
    year: '',
    startDay: '',
    startMonth: '',
    endDay: '',
    endMonth: '',
    city: '',
    country: '',
    url: ''
  });
  const [result, setResult] = useState(null);
  const [liveResult, setLiveResult] = useState(null);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Genera una vista previa en tiempo real
  useEffect(() => {
    if (authors.length > 0 && formData.title && formData.eventName && formData.year) {
      generatePreview();
    } else {
      setLiveResult(null);
    }
  }, [authors, formData]);

  const formatEventDate = () => {
    const { year, startMonth, startDay, endMonth, endDay } = formData;
    if (!year || !startMonth || !startDay) return year;

    const monthNames = {
      '01': 'enero', '02': 'febrero', '03': 'marzo', '04': 'abril',
      '05': 'mayo', '06': 'junio', '07': 'julio', '08': 'agosto',
      '09': 'septiembre', '10': 'octubre', '11': 'noviembre', '12': 'diciembre'
    };

    const startDate = `${parseInt(startDay)} de ${monthNames[startMonth]}`;
    
    if (endMonth && endDay) {
      if (startMonth === endMonth) {
        return `${year}, ${parseInt(startDay)}-${parseInt(endDay)} de ${monthNames[startMonth]}`;
      } else {
        const endDate = `${parseInt(endDay)} de ${monthNames[endMonth]}`;
        return `${year}, ${startDate} al ${endDate}`;
      }
    }
    
    return `${year}, ${startDate}`;
  };

  const generatePreview = () => {
    // Verificar si hay autores institucionales con siglas
    const institutionalAuthors = authors.filter(
      author => author.type === 'institutional' && author.abbreviation
    );

    let narrativeCitation, parentheticalCitation, narrativeSubsequent, parentheticalSubsequent;

    if (institutionalAuthors.length === 1 && (authorType === 'institutional' || authorType === 'both')) {
      // Para cita narrativa con institucional con sigla: "Nombre (SIGLA, año)"
      narrativeCitation = `${institutionalAuthors[0].name} (${institutionalAuthors[0].abbreviation}, ${formData.year})`;
      
      // Para cita parentética: "(Nombre [SIGLA], año)"
      parentheticalCitation = `(${institutionalAuthors[0].name} [${institutionalAuthors[0].abbreviation}], ${formData.year})`;
      
      // Para cita narrativa subsecuente: "SIGLA (año)"
      narrativeSubsequent = `${institutionalAuthors[0].abbreviation} (${formData.year})`;
      
      // Para cita parentética subsecuente: "(SIGLA, año)"
      parentheticalSubsequent = `(${institutionalAuthors[0].abbreviation}, ${formData.year})`;
    } else {
      // Caso normal sin autor institucional con sigla o múltiples autores
      const authorsForCitation = formatAuthorsForCitation(authors, authorType);
      const authorsForParenthetical = formatAuthorsForParenthetical(authors, authorType);
      const authorsForCitationSubsequent = formatAuthorsForCitation(authors, authorType, false);
      const authorsForParentheticalSubsequent = formatAuthorsForParenthetical(authors, authorType, false);

      narrativeCitation = `${authorsForCitation} (${formData.year})`;
      parentheticalCitation = `(${authorsForParenthetical}, ${formData.year})`;
      narrativeSubsequent = `${authorsForCitationSubsequent} (${formData.year})`;
      parentheticalSubsequent = `(${authorsForParentheticalSubsequent}, ${formData.year})`;
    }

    const citation = {
      narrative: narrativeCitation,
      parenthetical: parentheticalCitation,
      narrativeSubsequent: narrativeSubsequent,
      parentheticalSubsequent: parentheticalSubsequent
    };

    const authorsForReference = formatAuthorsForReference(authors, authorType);
    const eventDate = formatEventDate();
    const url = formatURL(formData.url);

    let referenceText = `${authorsForReference} (${eventDate}). <em>${formData.title}</em> [${formData.presentationType}]. ${formData.eventName}`;
    
    if (formData.city) {
      referenceText += `, ${formData.city}`;
      if (formData.country) {
        referenceText += `, ${formData.country}`;
      }
    }
    
    if (url) {
      referenceText += `. ${url}`;
    } else {
      referenceText += '.';
    }

    const reference = {
      html: referenceText,
      text: referenceText.replace(/<[^>]*>/g, '')
    };

    setLiveResult({ citation, reference });
  };

  const generateCitation = () => {
    if (authors.length === 0 || !formData.title || !formData.eventName || !formData.year) {
      alert('Por favor completa todos los campos obligatorios');
      return;
    }

    // Verificar si hay autores institucionales con siglas
    const institutionalAuthors = authors.filter(
      author => author.type === 'institutional' && author.abbreviation
    );

    let narrativeCitation, parentheticalCitation, narrativeSubsequent, parentheticalSubsequent;

    if (institutionalAuthors.length === 1 && (authorType === 'institutional' || authorType === 'both')) {
      // Para cita narrativa con institucional con sigla: "Nombre (SIGLA, año)"
      narrativeCitation = `${institutionalAuthors[0].name} (${institutionalAuthors[0].abbreviation}, ${formData.year})`;
      
      // Para cita parentética: "(Nombre [SIGLA], año)"
      parentheticalCitation = `(${institutionalAuthors[0].name} [${institutionalAuthors[0].abbreviation}], ${formData.year})`;
      
      // Para cita narrativa subsecuente: "SIGLA (año)"
      narrativeSubsequent = `${institutionalAuthors[0].abbreviation} (${formData.year})`;
      
      // Para cita parentética subsecuente: "(SIGLA, año)"
      parentheticalSubsequent = `(${institutionalAuthors[0].abbreviation}, ${formData.year})`;
    } else {
      // Caso normal sin autor institucional con sigla o múltiples autores
      const authorsForCitation = formatAuthorsForCitation(authors, authorType);
      const authorsForParenthetical = formatAuthorsForParenthetical(authors, authorType);
      const authorsForCitationSubsequent = formatAuthorsForCitation(authors, authorType, false);
      const authorsForParentheticalSubsequent = formatAuthorsForParenthetical(authors, authorType, false);

      narrativeCitation = `${authorsForCitation} (${formData.year})`;
      parentheticalCitation = `(${authorsForParenthetical}, ${formData.year})`;
      narrativeSubsequent = `${authorsForCitationSubsequent} (${formData.year})`;
      parentheticalSubsequent = `(${authorsForParentheticalSubsequent}, ${formData.year})`;
    }

    const citation = {
      narrative: narrativeCitation,
      parenthetical: parentheticalCitation,
      narrativeSubsequent: narrativeSubsequent,
      parentheticalSubsequent: parentheticalSubsequent
    };

    const authorsForReference = formatAuthorsForReference(authors, authorType);
    const eventDate = formatEventDate();
    const url = formatURL(formData.url);

    let referenceText = `${authorsForReference} (${eventDate}). <em>${formData.title}</em> [${formData.presentationType}]. ${formData.eventName}`;
    
    if (formData.city) {
      referenceText += `, ${formData.city}`;
      if (formData.country) {
        referenceText += `, ${formData.country}`;
      }
    }
    
    if (url) {
      referenceText += `. ${url}`;
    } else {
      referenceText += '.';
    }

    const reference = {
      html: referenceText,
      text: referenceText.replace(/<[^>]*>/g, '')
    };

    setResult({ citation, reference });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 dark:text-gray-100">Evento</h1>
        <p className="text-gray-600 dark:text-gray-400">Conferencias, ponencias, pósteres</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6 dark:bg-gray-800 dark:border-gray-700">
          <AuthorFields
            authors={authors}
            setAuthors={setAuthors}
            authorType={authorType}
            setAuthorType={setAuthorType}
            label="Presentadores"
          />

          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Título de la presentación *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              required
            />
          </div>

          <div>
            <label htmlFor="presentationType" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Tipo de presentación
            </label>
            <select
              id="presentationType"
              name="presentationType"
              value={formData.presentationType}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            >
              <option value="Ponencia">Ponencia</option>
              <option value="Conferencia">Conferencia</option>
              <option value="Póster">Póster</option>
            </select>
          </div>

          <div>
            <label htmlFor="eventName" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Nombre del evento *
            </label>
            <input
              type="text"
              id="eventName"
              name="eventName"
              value={formData.eventName}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
              Fechas del evento
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1 dark:text-gray-400">Fecha de inicio</label>
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="number"
                    name="year"
                    value={formData.year}
                    onChange={handleInputChange}
                    placeholder="Año"
                    className="px-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                    required
                  />
                  <input
                    type="number"
                    name="startDay"
                    value={formData.startDay}
                    onChange={handleInputChange}
                    placeholder="Día"
                    min="1"
                    max="31"
                    className="px-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  />
                  <select
                    name="startMonth"
                    value={formData.startMonth}
                    onChange={handleInputChange}
                    className="px-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  >
                    <option value="">Mes</option>
                    <option value="01">Ene</option>
                    <option value="02">Feb</option>
                    <option value="03">Mar</option>
                    <option value="04">Abr</option>
                    <option value="05">May</option>
                    <option value="06">Jun</option>
                    <option value="07">Jul</option>
                    <option value="08">Ago</option>
                    <option value="09">Sep</option>
                    <option value="10">Oct</option>
                    <option value="11">Nov</option>
                    <option value="12">Dic</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1 dark:text-gray-400">Fecha de fin (opcional)</label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="number"
                    name="endDay"
                    value={formData.endDay}
                    onChange={handleInputChange}
                    placeholder="Día"
                    min="1"
                    max="31"
                    className="px-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  />
                  <select
                    name="endMonth"
                    value={formData.endMonth}
                    onChange={handleInputChange}
                    className="px-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  >
                    <option value="">Mes</option>
                    <option value="01">Ene</option>
                    <option value="02">Feb</option>
                    <option value="03">Mar</option>
                    <option value="04">Abr</option>
                    <option value="05">May</option>
                    <option value="06">Jun</option>
                    <option value="07">Jul</option>
                    <option value="08">Ago</option>
                    <option value="09">Sep</option>
                    <option value="10">Oct</option>
                    <option value="11">Nov</option>
                    <option value="12">Dic</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Ciudad
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              />
            </div>
            <div>
              <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                País
              </label>
              <input
                type="text"
                id="country"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              />
            </div>
          </div>

          <div>
            <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              URL
            </label>
            <input
              type="url"
              id="url"
              name="url"
              value={formData.url}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            />
          </div>

          <button
            type="button"
            onClick={generateCitation}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Generar cita y referencia
          </button>
        </div>

        <div className="space-y-6">
          {!result && liveResult && (
            <CitationResult
              citation={liveResult.citation}
              reference={liveResult.reference}
              livePreview={true}
            />
          )}
          <CitationResult
            citation={result?.citation}
            reference={result?.reference}
          />
        </div>
      </div>
    </div>
  );
}

export default EventForm;